
import { Network } from '@wren/shared';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { isMobile } from '../../lib/platform';
import { makeVerboseDate } from '../../lib/time';
import { colors, spacing } from '../../style/theme';
import { BlogPostListItem } from '../../types';
import { ArrowNavButton } from '../buttons/ArrowNavButton';
import Flex from '../UI/Flex';
import HorizontalSeparator from '../UI/HorizontalSeparator';
import { Citation, Paragraph } from '../UI/TextStyles';

export function ProjectUpdate({
  post,
  verticalOrHorizontal,
  compact = false,
}: {
  post: BlogPostListItem;
  verticalOrHorizontal?: 'vertical' | 'horizontal';
  compact?: boolean;
}) {
  const isHorizontal = verticalOrHorizontal === 'horizontal';
  return (
    <Link to={`/blog/${post.slug}`} css={{ textDecoration: 'none' }}>
      <Flex
        direction={isHorizontal ? 'column' : 'row'}
        css={
          isHorizontal
            ? {
                padding: '10px',
                gap: compact ? 8 : 16,
                backgroundColor: 'rgba(244, 246, 250, 1)',
                borderRadius: 4,
                height: '100%',
              }
            : { padding: compact ? '5px 0' : '20px 0' }
        }
      >
        <img
          src={
            post.feature_image?.replace?.('w=2000', 'w=200') ??
            post.feature_image
          }
          alt={post.title}
          css={
            isHorizontal
              ? {
                  width: '100%',
                  height: 120,
                  borderRadius: 4,
                  objectFit: 'cover',
                }
              : {
                  margin: compact
                    ? `0 ${spacing.default}px 0 0`
                    : `${spacing.xSmall}px ${spacing.medium}px 0 0`,
                  border: '0.5px solid var(--gray-6)',
                  borderRadius: 4,
                  width: 90,
                  height: 90,
                  objectFit: 'cover',
                }
          }
        />
        <div>
          <Citation css={{ color: 'var(--gray-3)' }}>
            {makeVerboseDate(new Date(post.published_at))}
          </Citation>
          <Paragraph css={{ fontWeight: '700! important', margin: '4px 0' }}>
            {post.title}
          </Paragraph>
        </div>
      </Flex>
    </Link>
  );
}

export function ProjectUpdates({
  tags,
  hideButton = false,
  verticalOrHorizontal = 'vertical',
  ignoreSlug,
  compact = false,
}: {
  tags?: string[];
  hideButton?: boolean;
  verticalOrHorizontal?: 'vertical' | 'horizontal';
  ignoreSlug?: string; // if passed, will exclude this slug from the list of updates (useful for not showing the current blog post in the "you might also like" section)
  compact?: boolean;
}) {
  verticalOrHorizontal = isMobile() ? 'vertical' : verticalOrHorizontal; // always vertical if mobile
  const isHorizontal = verticalOrHorizontal === 'horizontal';

  const [updates, setUpdates] = useState<BlogPostListItem[]>([]);

  useEffect(() => {
    const getUpdates = async () => {
      const [response, responseBody] = await Network.get<{
        posts: BlogPostListItem[];
      }>(`blog/posts`, {
        tagsAny: tags
          ? tags.map((t) => t.toLowerCase().replace(/\s+/g, '-')).join(',')
          : '',
      });

      if (response.ok && responseBody) {
        setUpdates(
          responseBody.posts.filter((post) => post.slug !== ignoreSlug)
        ); // filter out the current blog post if ignoreSlug is passed
      }
    };

    if (tags) {
      getUpdates();
    }
  }, [tags]);

  return (
    <>
      <Flex
        direction={isHorizontal ? 'row' : 'column'}
        css={{
          gap: compact ? 4 : 16,
          marginBottom: compact ? 0 : 26,
        }}
      >
        {updates.slice(0, 3).map((post, i) => (
          <span
            key={post.id}
            css={{
              flex: isHorizontal ? '1' : 'unset',
            }}
          >
            <ProjectUpdate
              compact={compact}
              post={post}
              verticalOrHorizontal={verticalOrHorizontal}
            />
            {!isHorizontal && i !== updates.slice(0, 3).length - 1 && (
              <HorizontalSeparator />
            )}
          </span>
        ))}
      </Flex>
      {!hideButton && (
        <ArrowNavButton
          buttonType="text"
          color={colors.linkBlue}
          href="/blog"
          css={{ padding: '0px !important', marginTop: 10 }}
        >
          See all updates
        </ArrowNavButton>
      )}
    </>
  );
}
